import { useEffect, useState } from "react";
import { api } from "../lib/api";
import ProfileCard from "../components/ProfileCard";
import { Search } from "lucide-react";

const RELIGIONS = ["", "Hindu", "Muslim", "Christian", "Sikh", "Jain", "Buddhist", "Other"];

export default function Browse() {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ religion: "", city: "", min_age: "", max_age: "", q: "" });

  const fetchProfiles = async (params = {}) => {
    setLoading(true);
    try {
      const { data } = await api.get("/profiles", { params: Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v != null)) });
      setProfiles(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchProfiles(); }, []);

  const submit = (e) => { e.preventDefault(); fetchProfiles(filters); };

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16" data-testid="browse-page">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
        <div>
          <p className="overline mb-3">Discover</p>
          <h1 className="font-serif-display text-4xl md:text-5xl tracking-tight">A small gallery of possibilities.</h1>
        </div>
        <p className="text-sm text-[#8C857B] max-w-sm">Profiles below are shown opposite to your gender. Use filters to narrow with intent.</p>
      </div>

      <form onSubmit={submit} className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-10">
        <div className="col-span-2 md:col-span-2 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8C857B]" strokeWidth={1.5} />
          <input
            data-testid="browse-search-input"
            placeholder="Search name, profession…"
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            className="w-full rounded-xl border border-[#E2DCD0] bg-white pl-9 pr-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C85A44]/30"
          />
        </div>
        <select data-testid="browse-religion-select" value={filters.religion} onChange={(e) => setFilters({ ...filters, religion: e.target.value })}
          className="rounded-xl border border-[#E2DCD0] bg-white px-3 py-2.5 text-sm">
          {RELIGIONS.map((r) => <option key={r} value={r}>{r || "Any religion"}</option>)}
        </select>
        <input data-testid="browse-city-input" placeholder="City" value={filters.city} onChange={(e) => setFilters({ ...filters, city: e.target.value })}
          className="rounded-xl border border-[#E2DCD0] bg-white px-3 py-2.5 text-sm" />
        <input data-testid="browse-min-age" type="number" min={18} max={80} placeholder="Min age" value={filters.min_age} onChange={(e) => setFilters({ ...filters, min_age: e.target.value })}
          className="rounded-xl border border-[#E2DCD0] bg-white px-3 py-2.5 text-sm" />
        <input data-testid="browse-max-age" type="number" min={18} max={80} placeholder="Max age" value={filters.max_age} onChange={(e) => setFilters({ ...filters, max_age: e.target.value })}
          className="rounded-xl border border-[#E2DCD0] bg-white px-3 py-2.5 text-sm" />
        <button type="submit" data-testid="browse-filter-button" className="col-span-2 md:col-span-6 md:w-auto md:justify-self-end rounded-full bg-[#2C2A28] hover:bg-black text-white px-6 py-2.5 text-sm transition">
          Apply filters
        </button>
      </form>

      {loading ? (
        <div className="text-center py-32 text-[#8C857B]">Curating profiles…</div>
      ) : profiles.length === 0 ? (
        <div className="text-center py-32 border border-dashed border-[#E2DCD0] rounded-2xl" data-testid="browse-empty">
          <h3 className="font-serif-display text-2xl mb-2">No matches yet.</h3>
          <p className="text-sm text-[#8C857B]">Try adjusting your filters — or invite a friend to join.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-10">
          {profiles.map((p, i) => <ProfileCard key={p.id} profile={p} index={i} />)}
        </div>
      )}
    </div>
  );
}
